import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { expenditureAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Receipt, Search, TrendingDown, TrendingUp, FolderOpen, Store,
  Package, ShoppingCart, Loader2, ArrowUpDown,
} from 'lucide-react';

const formatCurrency = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLORS = {
  placed: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-amber-100 text-amber-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  planned: 'bg-gray-100 text-gray-700',
  approved: 'bg-green-100 text-green-700',
  spent: 'bg-blue-100 text-blue-700',
};

export default function Expenditure() {
  const { isAdmin, isStaff } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('items');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter !== 'all') params.project_id = projectFilter;
      if (vendorFilter !== 'all') params.vendor_id = vendorFilter;
      const res = await expenditureAPI.getSummary(params);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load expenditure:', err);
    } finally {
      setLoading(false);
    }
  }, [projectFilter, vendorFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const summary = data?.summary || {};
  const lineItems = data?.line_items || [];
  const budgetItems = data?.budget_items || [];
  const byVendor = data?.by_vendor || [];
  const byProject = data?.by_project || [];
  const filterProjects = data?.filters?.projects || [];
  const filterVendors = data?.filters?.vendors || [];

  // Filter line items by search
  const filteredLineItems = lineItems.filter(li => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return li.item_name?.toLowerCase().includes(term) ||
      li.vendor_name?.toLowerCase().includes(term) ||
      li.project_name?.toLowerCase().includes(term) ||
      li.part_number?.toLowerCase().includes(term);
  });

  // Sort line items
  const sortedLineItems = [...filteredLineItems].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'total') return (a.total - b.total) * dir;
    if (sortField === 'date') return (new Date(a.date || 0) - new Date(b.date || 0)) * dir;
    if (sortField === 'item') return (a.item_name || '').localeCompare(b.item_name || '') * dir;
    if (sortField === 'vendor') return (a.vendor_name || '').localeCompare(b.vendor_name || '') * dir;
    return 0;
  });

  // Filter budget items by search
  const filteredBudgetItems = budgetItems.filter(bi => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return bi.item_name?.toLowerCase().includes(term) ||
      bi.vendor_name?.toLowerCase().includes(term) ||
      bi.project_name?.toLowerCase().includes(term) ||
      bi.category?.toLowerCase().includes(term);
  });

  const totalSpend = summary.total_order_spend + summary.total_budget_actual;

  return (
    <Layout>
      <div className="space-y-6" data-testid="expenditure-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
            <Receipt className="h-7 w-7 text-muted-foreground" />
            Expenditure Tracker
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all spending across vendor orders and budget items
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Spend</p>
              <p className="text-2xl font-light mt-1" data-testid="total-spend">{formatCurrency(totalSpend)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendor Orders</p>
              <p className="text-2xl font-light mt-1">{formatCurrency(summary.total_order_spend)}</p>
              <p className="text-xs text-muted-foreground mt-1">{summary.total_orders} orders, {summary.total_line_items} items</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Budget Estimated</p>
              <p className="text-2xl font-light mt-1">{formatCurrency(summary.total_budget_estimated)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Budget Actual</p>
              <p className="text-2xl font-light mt-1">{formatCurrency(summary.total_budget_actual)}</p>
              {summary.total_budget_estimated > 0 && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${summary.total_budget_actual <= summary.total_budget_estimated ? 'text-green-600' : 'text-red-600'}`}>
                  {summary.total_budget_actual <= summary.total_budget_estimated ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                  {formatCurrency(Math.abs(summary.total_budget_estimated - summary.total_budget_actual))} {summary.total_budget_actual <= summary.total_budget_estimated ? 'under' : 'over'} budget
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search items, vendors, projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="expenditure-search"
                />
              </div>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full sm:w-52" data-testid="expenditure-project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {filterProjects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger className="w-full sm:w-52" data-testid="expenditure-vendor-filter">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {filterVendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed Views */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="items" data-testid="tab-items">
              <Package className="h-4 w-4 mr-2" />Item-wise ({sortedLineItems.length})
            </TabsTrigger>
            <TabsTrigger value="budget" data-testid="tab-budget">
              <Receipt className="h-4 w-4 mr-2" />Budget ({filteredBudgetItems.length})
            </TabsTrigger>
            <TabsTrigger value="vendor" data-testid="tab-vendor">
              <Store className="h-4 w-4 mr-2" />By Vendor ({byVendor.length})
            </TabsTrigger>
            <TabsTrigger value="project" data-testid="tab-project">
              <FolderOpen className="h-4 w-4 mr-2" />By Project ({byProject.length})
            </TabsTrigger>
          </TabsList>

          {/* Item-wise view */}
          <TabsContent value="items" className="mt-4">
            {sortedLineItems.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><ShoppingCart className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{searchTerm ? 'No items match your search' : 'No vendor order items found'}</p></CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs cursor-pointer" onClick={() => toggleSort('item')}>
                          <span className="flex items-center gap-1">Item <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                        <TableHead className="text-xs cursor-pointer" onClick={() => toggleSort('vendor')}>
                          <span className="flex items-center gap-1">Vendor <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                        <TableHead className="text-xs">Project</TableHead>
                        <TableHead className="text-xs text-center">Qty</TableHead>
                        <TableHead className="text-xs text-right">Unit Price</TableHead>
                        <TableHead className="text-xs text-right cursor-pointer" onClick={() => toggleSort('total')}>
                          <span className="flex items-center gap-1 justify-end">Total <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs cursor-pointer" onClick={() => toggleSort('date')}>
                          <span className="flex items-center gap-1">Date <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedLineItems.map((li, idx) => (
                        <TableRow key={`${li.order_id}-${idx}`} data-testid={`expenditure-item-${idx}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{li.item_name}</p>
                              {li.part_number && <p className="text-xs text-muted-foreground font-mono">{li.part_number}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Link to={`/vendors/${li.vendor_id}`} className="text-sm hover:text-primary transition-colors">
                              {li.vendor_name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Link to={`/projects/${li.project_id}`} className="text-sm hover:text-primary transition-colors">
                              {li.project_name}
                            </Link>
                          </TableCell>
                          <TableCell className="text-center text-sm">{li.quantity}</TableCell>
                          <TableCell className="text-right text-sm">{formatCurrency(li.unit_price)}</TableCell>
                          <TableCell className="text-right font-medium text-sm">{formatCurrency(li.total)}</TableCell>
                          <TableCell>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[li.status] || 'bg-gray-100 text-gray-700'}`}>
                              {li.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDate(li.date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Total row */}
                  <div className="flex justify-end px-4 py-3 border-t bg-muted/30">
                    <span className="text-sm font-medium">
                      Total: {formatCurrency(sortedLineItems.reduce((s, li) => s + li.total, 0))}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Budget view */}
          <TabsContent value="budget" className="mt-4">
            {filteredBudgetItems.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Receipt className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{searchTerm ? 'No budget items match your search' : 'No budget items found'}</p></CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Description</TableHead>
                        <TableHead className="text-xs">Project</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">Vendor</TableHead>
                        <TableHead className="text-xs text-right">Estimated</TableHead>
                        <TableHead className="text-xs text-right">Actual</TableHead>
                        <TableHead className="text-xs text-right">Variance</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBudgetItems.map((bi, idx) => {
                        const variance = bi.estimated_cost - bi.actual_cost;
                        return (
                          <TableRow key={bi.budget_item_id || idx} data-testid={`budget-item-${idx}`}>
                            <TableCell className="font-medium text-sm">{bi.item_name}</TableCell>
                            <TableCell>
                              <Link to={`/projects/${bi.project_id}`} className="text-sm hover:text-primary transition-colors">
                                {bi.project_name}
                              </Link>
                            </TableCell>
                            <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded-full bg-muted">{bi.category}</span></TableCell>
                            <TableCell className="text-sm">{bi.vendor_name || '—'}</TableCell>
                            <TableCell className="text-right text-sm">{formatCurrency(bi.estimated_cost)}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{formatCurrency(bi.actual_cost)}</TableCell>
                            <TableCell className={`text-right text-sm font-medium ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                            </TableCell>
                            <TableCell>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[bi.status] || 'bg-gray-100 text-gray-700'}`}>
                                {bi.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* By Vendor view */}
          <TabsContent value="vendor" className="mt-4">
            {byVendor.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Store className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No vendor spending data</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {byVendor.map(v => (
                  <Card key={v.vendor_id} data-testid={`vendor-spend-${v.vendor_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-muted rounded">
                            <Store className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <Link to={`/vendors/${v.vendor_id}`} className="font-medium hover:text-primary transition-colors">
                              {v.vendor_name}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {v.order_count} order{v.order_count !== 1 ? 's' : ''} &middot; {v.items} item{v.items !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-light">{formatCurrency(v.total)}</p>
                          <p className="text-xs text-muted-foreground">
                            {totalSpend > 0 ? Math.round((v.total / summary.total_order_spend) * 100) : 0}% of orders
                          </p>
                        </div>
                      </div>
                      {/* Simple bar */}
                      {summary.total_order_spend > 0 && (
                        <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary/60 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (v.total / summary.total_order_spend) * 100)}%` }}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* By Project view */}
          <TabsContent value="project" className="mt-4">
            {byProject.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No project spending data</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {byProject.map(p => {
                  const projectTotal = p.order_total + p.budget_actual;
                  return (
                    <Card key={p.project_id} data-testid={`project-spend-${p.project_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Link to={`/projects/${p.project_id}`} className="font-medium hover:text-primary transition-colors flex items-center gap-2">
                            <FolderOpen className="h-4 w-4 text-muted-foreground" />
                            {p.project_name}
                          </Link>
                          <p className="text-lg font-light">{formatCurrency(projectTotal)}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-2 bg-muted/50 rounded">
                            <p className="text-xs text-muted-foreground">Vendor Orders</p>
                            <p className="font-medium text-sm mt-1">{formatCurrency(p.order_total)}</p>
                          </div>
                          <div className="p-2 bg-muted/50 rounded">
                            <p className="text-xs text-muted-foreground">Budget Est.</p>
                            <p className="font-medium text-sm mt-1">{formatCurrency(p.budget_estimated)}</p>
                          </div>
                          <div className="p-2 bg-muted/50 rounded">
                            <p className="text-xs text-muted-foreground">Budget Actual</p>
                            <p className={`font-medium text-sm mt-1 ${p.budget_actual > p.budget_estimated && p.budget_estimated > 0 ? 'text-red-600' : ''}`}>
                              {formatCurrency(p.budget_actual)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
